import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useMemo, useRef } from 'react';
import type { SparseArray } from 'utils/types/SparseArray';

const LOAD_EXTRA_DEFAULT = 50;
/**
 * Currently set to 1 (no simultaneous) because anything more is suicide on ArangoDB.
 * Once we migrate to PostgreSQL, we can experiment with this number again.
 *
 * @see Props.maxSimultaneousRequests
 */
const MAX_SIMULTANEOUS_REQUESTS_DEFAULT = 1;

interface Props {
  /**
   * Sparse list of loaded rows
   *
   * Not loaded rows must be represented by undefined value to make this hook work.
   *
   * Also, we need to provide the synchronous always up-to-date value, this is why it is
   * a function and not a value directly. Otherwise, the following can happen: one request
   * ends, clears up `loadingRows` (see implementation of this hook), but the result did not
   * arrive yet reactively via updated `loadedRows`. When another request is fired to load
   * similar page at that time, this hook cannot see these data are already either loading or loaded,
   * therefore a duplicate request for the same content would be fired.
   */
  getLoadedRows: () => SparseArray<unknown | undefined>;
  /**
   * Defer loading chunks when given amount of requests is already running.
   *
   * Default = MAX_SIMULTANEOUS_REQUESTS_DEFAULT
   * Some reasonable number of simultaneous requests will give faster UI response,
   * but we need to cap it because too many simultaneous requests due to wild scrolling may
   * clog both server and UI.
   */
  maxSimultaneousRequests?: number;
  /**
   * When missing data are detected, this event is called to load data to cover current
   * viewport + `loadExtra` rows around.
   *
   * Note that total data size is not checked (may not even be known before first load),
   * so startIndex + count may be greater than number of rows.
   */
  onLoadChunk: (startIndex: number, endIndex: number) => Promise<void>;
  /**
   * Number of extra records to load in a direction where we missing data till the end of requested range
   *
   * This feature is set to LOAD_EXTRA_DEFAULT by default as we expect a lower number of api requests for bigger pages
   * would provide better scrolling experience than always loading exactly only what is in the viewport.
   */
  loadExtra?: number;
  /**
   * Pass in total row count after first data load to prevent future onLoadChunk calls,
   * e.g. on viewport resize, repeatedly attempt to load non-existent data. Not needed
   * on first load when row count is not known yet, since first request is never redundant
   *
   * number: actual total number of rows in the table
   * null: actual total number is unknown
   */
  getRowCount: () => number | null;
}

export interface LazyLoadRowsHook {
  handleRequestRowRange: (startIndex: number, endIndex: number) => void;
}

/**
 * Prevent firing many requests at once when scrolling fast.
 *
 * When more than maxSimultaneousRequests are running, previous queued request
 * (if any) is dropped and the last request is enqueued.
 * This way it is ensured that the server is not bombed with requests
 * and when user finishes scrolling, the last viewport is filled.
 */
interface LoadQueue {
  runningRequests: number;
  next: null | {
    fromIndex: number;
    toIndex: number;
  };
}

/**
 * Provides row lazy loading logic for LazyRowContainer
 *
 * This hook provides logic for lazy loading data records.
 * Not to be confused with lazy rendering only of content in viewport.
 *
 * @example
 * const lazyLoadRows = useLazyLoadRows({
 *   loadedRows,
 *   rowCount,
 *   onLoadChunk: async (startIndex, endIndex) => {
 *     // logic that loads data from startIndex to endIndex (inclusive) into loadedRows
 *   }
 * });
 * ...
 * <LazyRowContainer
 *   ...
 *   rowCount={rowCount}
 *   onRequestRowRange={lazyLoadRows.handleRequestRowRange}
 * />
 */
export const useLazyLoadRows = ({
  loadExtra = LOAD_EXTRA_DEFAULT,
  maxSimultaneousRequests = MAX_SIMULTANEOUS_REQUESTS_DEFAULT,
  ...props
}: Props): LazyLoadRowsHook => {
  const loadingRows = useRef<Set<number>>(new Set()).current;
  const queue = useRef<LoadQueue>({ runningRequests: 0, next: null }).current;

  const loadRows = async (start: number, end: number) => {
    try {
      queue.runningRequests += 1;
      for (let i = start; i <= end; i += 1) {
        loadingRows.add(i);
      }

      await props.onLoadChunk(start, end);
    } finally {
      queue.runningRequests -= 1;
      for (let i = start; i <= end; i += 1) {
        loadingRows.delete(i);
      }
      if (queue.next) {
        const { fromIndex, toIndex } = queue.next;
        queue.next = null;
        handleRequestRowRange(fromIndex, toIndex);
      }
    }
  };

  const handleRequestRowRange = useStableCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
    (fromIndex: number, toIndex: number) => {
      if (queue.runningRequests >= maxSimultaneousRequests) {
        queue.next = { fromIndex, toIndex };
        return;
      }

      const rowCount = props.getRowCount();
      const cappedToIndex =
        rowCount !== null ? Math.min(rowCount - 1, toIndex) : toIndex;

      const loadedRows = props.getLoadedRows();
      const isMissingIndex = (index: number): boolean => {
        return loadedRows[index] === undefined && !loadingRows.has(index);
      };

      let firstUnavailable = null;
      let lastUnavailable!: number;
      for (let i = fromIndex; i <= cappedToIndex; i += 1) {
        if (isMissingIndex(i)) {
          if (firstUnavailable === null) {
            firstUnavailable = i;
          }
          lastUnavailable = i;
        }
      }
      if (firstUnavailable === null) {
        // nothing to lazy-load
        return;
      }
      // Check on which ends we are missing data to load extra records at that direction.
      const loadExtraAtStart = fromIndex > 0 && firstUnavailable === fromIndex;
      const loadExtraAtEnd = lastUnavailable === cappedToIndex;
      // If we should load extra rows in both direction, distribute the desired number of evenly
      // to keep the number of loaded rows similar to other loads and keep the UI similarly fast.
      const loadExtraOnSide =
        loadExtraAtEnd && loadExtraAtStart
          ? Math.ceil(loadExtra / 2)
          : loadExtra;
      if (loadExtraAtStart) {
        const minIndex = Math.max(0, firstUnavailable - loadExtraOnSide);
        for (let i = firstUnavailable - 1; i >= minIndex; i -= 1) {
          if (isMissingIndex(i)) {
            firstUnavailable = i;
          }
        }
      }
      if (loadExtraAtEnd) {
        const maxIndex = lastUnavailable + loadExtraOnSide;
        for (let i = lastUnavailable + 1; i <= maxIndex; i += 1) {
          if (isMissingIndex(i)) {
            lastUnavailable = i;
          }
        }
      }
      loadRows(firstUnavailable, lastUnavailable);
    },
  );

  return useMemo(
    () => ({
      handleRequestRowRange,
    }),
    [],
  );
};
